/**
 * UsagePage — user-facing dashboard showing plan, monthly quota usage,
 * progress bars, 80%/95%/100% warning banners, reset date.
 *
 * Pulls from /api/plan/current (single round-trip; no internal caps leaked).
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Zap, AlertTriangle, AlertOctagon, CheckCircle2, Users,
  MessageSquare, Workflow, Mail, Database, Crown, Sparkles, RefreshCw,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const QUOTA_META = {
  ai_conversations: { label: "AI conversations", icon: Sparkles, accent: "violet" },
  contacts: { label: "Contacts", icon: Users, accent: "blue" },
  funnels: { label: "Funnels", icon: Workflow, accent: "indigo" },
  emails: { label: "Emails sent", icon: Mail, accent: "rose" },
  storage_mb: { label: "Storage", icon: Database, accent: "amber",
    format: (v) => v >= 1024 ? `${(v/1024).toFixed(1)} GB` : `${Math.round(v)} MB` },
  team_members: { label: "Team members", icon: Users, accent: "teal" },
};

export default function UsagePage() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/plan/current");
      setSnap(r.data);
    } catch (e) {
      toast.error("Could not load usage");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const planName = snap?.plan?.name || "Free";
  const planSlug = snap?.plan?.slug || "free";
  const isAscend = planSlug === "ascend";
  const resetsAt = snap?.resets_at;

  // Find the highest utilization across all quotas → drives the global banner
  const highestPct = useMemo(() => {
    if (!snap?.usage) return 0;
    return Math.max(0, ...Object.values(snap.usage).map((u) => u.percent || 0));
  }, [snap]);

  const banner = useMemo(() => {
    if (!snap) return null;
    if (highestPct >= 100) return {
      kind: "critical",
      title: "You've reached a monthly limit",
      body: `Upgrade to ${isAscend ? "scale plan" : "ASCEND"} or wait for the reset on ${formatDate(resetsAt)}.`,
      icon: AlertOctagon,
    };
    if (highestPct >= 95) return {
      kind: "critical",
      title: "You're approaching your monthly limit",
      body: `You've used over 95% of one or more quotas. Limits reset on ${formatDate(resetsAt)}.`,
      icon: AlertOctagon,
    };
    if (highestPct >= 80) return {
      kind: "warning",
      title: "Heads up — usage is high",
      body: `You've used over 80% of one or more quotas. Limits reset on ${formatDate(resetsAt)}.`,
      icon: AlertTriangle,
    };
    return null;
  }, [snap, highestPct, isAscend, resetsAt]);

  if (loading) return <div className="p-10 text-meta">Loading usage…</div>;
  if (!snap) return <div className="p-10 text-meta">Usage unavailable.</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="usage-page">
      <div className="max-w-5xl mx-auto p-6 lg:p-10 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-meta-soft font-semibold mb-1">Plan & usage</p>
            <h1 className="font-display text-3xl font-bold text-ink">Your monthly allowance</h1>
            <p className="text-sm text-meta mt-1">
              You're on the <strong className="text-ink">{planName}</strong> plan
              {snap.plan.state === "trialing" && <> · Trial ends {formatDate(snap.plan.trial_ends_at)}</>}
              · Resets <strong className="text-ink">{formatDate(resetsAt)}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              data-testid="usage-refresh-btn"
              className="rounded-full border border-line bg-white px-3 py-1.5 text-xs text-meta hover:text-ink inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            {!isAscend && (
              <Link
                to="/settings?tab=billing"
                data-testid="usage-upgrade-btn"
                className="rounded-full ashra-gradient-bg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md"
              >
                <Crown className="h-4 w-4" /> Upgrade
              </Link>
            )}
          </div>
        </div>

        {/* Global banner */}
        {banner && (
          <div
            className={`rounded-2xl p-4 border ${
              banner.kind === "critical"
                ? "bg-rose-50 border-rose-200 text-rose-900"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`}
            data-testid="usage-banner"
          >
            <div className="flex items-start gap-3">
              <banner.icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">{banner.title}</p>
                <p className="text-xs mt-0.5">{banner.body}</p>
                {!isAscend && (
                  <Link to="/settings?tab=billing" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold underline">
                    Upgrade your plan →
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quota cards */}
        <div className="grid sm:grid-cols-2 gap-4" data-testid="usage-cards">
          {Object.entries(snap.usage).map(([key, u]) => {
            const meta = QUOTA_META[key];
            if (!meta) return null;
            return <QuotaCard key={key} kind={key} meta={meta} usage={u} />;
          })}
        </div>

        {/* Plan summary */}
        <div className="surface p-6" data-testid="plan-summary">
          <h2 className="font-display text-lg font-bold text-ink mb-3 inline-flex items-center gap-2">
            <Zap className="h-4 w-4 text-ai" /> What's included in {planName}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(snap.plan.features || {}).map(([feat, enabled]) => (
              <div key={feat} className="text-sm flex items-center gap-2">
                {enabled ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                ) : (
                  <span className="h-4 w-4 rounded-full bg-slate-200 flex-shrink-0" />
                )}
                <span className={enabled ? "text-ink" : "text-meta-soft line-through"}>
                  {feat.replace(/_/g, " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuotaCard({ kind, meta, usage }) {
  const Icon = meta.icon;
  const limit = usage.limit || 0;
  const used = usage.used || 0;
  const pct = usage.percent || 0;
  const blocked = usage.blocked;
  const fmt = meta.format || ((v) => Math.round(v).toLocaleString());

  const barColor = blocked || pct >= 95
    ? "bg-rose-500"
    : pct >= 80
      ? "bg-amber-500"
      : "bg-violet-500";

  return (
    <div className="surface p-5" data-testid={`quota-${kind}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`rounded-xl p-2 bg-${meta.accent}-50 text-${meta.accent}-600`}>
            <Icon className="h-4 w-4" />
          </div>
          <p className="text-sm font-semibold text-ink">{meta.label}</p>
        </div>
        {blocked && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
            Limit reached
          </span>
        )}
      </div>
      <div className="flex items-baseline justify-between mb-2">
        <p className="font-display text-2xl font-bold text-ink">{fmt(used)}</p>
        <p className="text-xs text-meta">of {fmt(limit)}</p>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${Math.min(100, pct)}%` }}
          data-testid={`quota-bar-${kind}`}
        />
      </div>
      <p className="text-[11px] text-meta-soft mt-2">
        {pct >= 100 ? "Quota reached" : `${(100 - pct).toFixed(0)}% remaining`}
      </p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
